import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { matchesSelectSearch } from "./selectSearch";

export interface YhSelectOption {
  label: string;
  value: string | number;
  /** Extra text matched by search (e.g. project uid PC_8853) */
  keywords?: string;
}

export interface YhSelectGroup {
  label: string;
  options: YhSelectOption[];
}

export interface YhSelectProps {
  label?: string;
  error?: string;
  options?: YhSelectOption[];
  /** Grouped options (e.g. Stage: Contact / Opportunity / Lead). When set, overrides flat options list. */
  groups?: YhSelectGroup[];
  placeholder?: string;
  requiredMark?: boolean;
  className?: string;
  id?: string;
  name?: string;
  value?: string | number;
  disabled?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Show spinner in the dropdown (e.g. while remote options load) */
  loading?: boolean;
  showOptionIcon?: boolean;
  /** Show a single clear (×) when a value is selected */
  clearable?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  /** Fires when the dropdown opens or closes */
  onOpenChange?: (open: boolean) => void;
  /** Fires as the user types in the search box (for async/remote search) */
  onSearchChange?: (query: string) => void;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  onKeyPress?: React.KeyboardEventHandler<HTMLButtonElement>;
}

/** Build grouped stage options from getStage() API payload. */
function stageTabIndex(stage: any) {
  const n = Number(stage?.tabIndex);
  return Number.isFinite(n) ? n : null;
}

function sortByTabIndex<T extends { tabIndex?: unknown }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const ai = stageTabIndex(a);
    const bi = stageTabIndex(b);
    if (ai != null && bi != null && ai !== bi) return ai - bi;
    if (ai != null && bi == null) return -1;
    if (ai == null && bi != null) return 1;
    return 0;
  });
}

export function buildStageSelectGroups(data: any[] = []): YhSelectGroup[] {
  return sortByTabIndex((data || []).filter((stage) => stage?.stage_Name)).map(
    (stage) => ({
      label: String(stage.stage_Name),
      options: sortByTabIndex(
        Array.isArray(stage.sub_Stage_name) ? stage.sub_Stage_name : []
      )
        .filter((sub: any) => sub?.stage_Name)
        .map((sub: any) => ({
          label: String(sub.stage_Name),
          value: String(sub.stage_Name),
        })),
    })
  ).filter((g) => g.options.length > 0);
}

const BuildingIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
    <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6M9 10h.01M15 10h.01M9 14h.01M15 14h.01" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={`transition-transform ${open ? "rotate-180" : ""}`}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const ClearIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const SpinnerIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    className="animate-spin text-[#A02448]"
  >
    <circle
      cx="12"
      cy="12"
      r="9"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeOpacity="0.25"
    />
    <path
      d="M21 12a9 9 0 00-9-9"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

/** Emit a select-like change event so existing `e.target.value` callers keep working. */
const emitChange = (
  onChange: YhSelectProps["onChange"],
  value: string,
  name?: string
) => {
  if (!onChange) return;
  const event = {
    target: { value, name: name || "" },
    currentTarget: { value, name: name || "" },
  } as unknown as React.ChangeEvent<HTMLSelectElement>;
  onChange(event);
};

type MenuPos = { top: number; left: number; width: number; maxHeight: number };

export const YhSelect: React.FC<YhSelectProps> = ({
  label,
  error,
  options = [],
  groups,
  placeholder = "Select",
  requiredMark,
  className = "",
  id,
  name,
  value,
  disabled,
  searchable = true,
  searchPlaceholder,
  loading = false,
  showOptionIcon = true,
  clearable = false,
  onChange,
  onOpenChange,
  onSearchChange,
  onBlur,
  onFocus,
  onKeyPress,
}) => {
  const reactId = useId();
  const selectId = id || name || reactId;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);
  const skipOpenNotify = useRef(true);
  const isGrouped = Array.isArray(groups) && groups.length > 0;

  const flatOptions = useMemo(() => {
    if (isGrouped) {
      return groups!.flatMap((g) => g.options);
    }
    return options;
  }, [isGrouped, groups, options]);

  const selected = useMemo(
    () => flatOptions.find((o) => String(o.value) === String(value ?? "")),
    [flatOptions, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return options;
    return options.filter((o) =>
      matchesSelectSearch(o.label, o.value, q, o.keywords)
    );
  }, [options, query]);

  const filteredGroups = useMemo(() => {
    if (!isGrouped) return [];
    const q = query.trim();
    if (!q) return groups!;
    return groups!
      .map((g) => ({
        ...g,
        options: g.options.filter(
          (o) =>
            matchesSelectSearch(o.label, o.value, q, o.keywords) ||
            matchesSelectSearch(g.label, "", q)
        ),
      }))
      .filter((g) => g.options.length > 0);
  }, [isGrouped, groups, query]);

  const updateMenuPos = () => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom - gap - 8;
    const spaceAbove = rect.top - gap - 8;
    const preferBelow = spaceBelow >= 160 || spaceBelow >= spaceAbove;
    const preferredMax = isGrouped ? 360 : 280;
    const maxHeight = Math.min(
      preferredMax,
      Math.max(120, preferBelow ? spaceBelow : spaceAbove)
    );
    setMenuPos({
      top: preferBelow ? rect.bottom + gap : Math.max(8, rect.top - gap - maxHeight),
      left: rect.left,
      width: rect.width,
      maxHeight,
    });
  };

  useEffect(() => {
    if (skipOpenNotify.current) {
      skipOpenNotify.current = false;
      return;
    }
    onOpenChange?.(open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    updateMenuPos();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onReposition = () => updateMenuPos();
    document.addEventListener("mousedown", onDoc, true);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    requestAnimationFrame(() => searchRef.current?.focus());
    return () => {
      document.removeEventListener("mousedown", onDoc, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open]);

  const handleSelect = (nextValue: string) => {
    if (String(nextValue) === String(value ?? "")) {
      setOpen(false);
      return;
    }
    emitChange(onChange, nextValue, name);
    setOpen(false);
  };

  const hasValue =
    value !== undefined &&
    value !== null &&
    String(value).trim() !== "";
  const displayLabel = selected?.label || (hasValue ? String(value) : placeholder);
  const isPlaceholder = !selected && !hasValue;
  const showClear = clearable && hasValue && !disabled;

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    emitChange(onChange, "", name);
    setOpen(false);
  };

  const menu =
    open && menuPos
      ? createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              maxHeight: menuPos.maxHeight,
              zIndex: 10000,
            }}
            className="overflow-hidden rounded-xl border border-[#EEF0F5] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.12)]"
          >
            {searchable ? (
              <div className="border-b border-[#EEF0F5] p-2.5">
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]">
                    <SearchIcon />
                  </span>
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    autoComplete="off"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const next = e.target.value;
                      setQuery(next);
                      onSearchChange?.(next);
                    }}
                    placeholder={
                      searchPlaceholder ||
                      `Search ${label ? label.toLowerCase() : "options"}...`
                    }
                    className="w-full rounded-lg border border-[#E5E7EB] bg-white py-2 pl-9 pr-9 text-sm text-[#111827] outline-none placeholder:text-[#9CA3AF] focus:border-[#841C39]"
                  />
                  {loading ? (
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                      <SpinnerIcon />
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}

            {loading && !searchable ? (
              <div className="flex items-center justify-center gap-2 px-3 py-4 text-sm text-[#6B7280]">
                <SpinnerIcon />
                Loading…
              </div>
            ) : null}

            <ul
              role="listbox"
              className="overflow-y-auto py-1 [scrollbar-width:thin]"
              style={{
                maxHeight: searchable
                  ? menuPos.maxHeight - 56
                  : menuPos.maxHeight,
              }}
            >
              {loading && searchable && filtered.length === 0 && filteredGroups.length === 0 ? (
                <li className="flex items-center justify-center gap-2 px-3 py-4 text-sm text-[#6B7280]">
                  <SpinnerIcon />
                  Searching…
                </li>
              ) : null}
              {isGrouped ? (
                <>
                  {selected ? (
                    <li className="border-b border-[#EEF0F5] px-1 pb-1">
                      <div className="rounded-lg bg-[#F9F0F4] px-3 py-2.5 text-sm font-medium text-[#4338CA]">
                        {selected.label}
                      </div>
                    </li>
                  ) : null}

                  {filteredGroups.length === 0 ? (
                    <li className="px-3 py-4 text-center text-sm text-[#9CA3AF]">
                      No results found
                    </li>
                  ) : (
                    filteredGroups.map((group) => (
                      <li key={group.label} className="pt-1">
                        <div className="px-3 py-1.5 text-sm font-bold text-[#111827]">
                          {group.label}
                        </div>
                        <ul>
                          {group.options.map((opt) => {
                            const isActive =
                              String(opt.value) === String(value ?? "");
                            return (
                              <li key={String(opt.value)}>
                                <button
                                  type="button"
                                  role="option"
                                  aria-selected={isActive}
                                  className={`flex w-full items-center px-3 py-2 text-left text-sm transition ${
                                    isActive
                                      ? "bg-[#F5F3FF] font-medium text-[#4338CA]"
                                      : "text-[#111827] hover:bg-[#F8FAFC]"
                                  }`}
                                  onClick={() =>
                                    handleSelect(String(opt.value))
                                  }
                                >
                                  <span className="truncate">{opt.label}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    ))
                  )}
                </>
              ) : (
                <>
                  {placeholder ? (
                    <li>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isPlaceholder}
                        className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm transition ${
                          isPlaceholder
                            ? "bg-[#F5F3FF] font-medium text-[#A02448]"
                            : "text-[#6B7280] hover:bg-[#F8FAFC]"
                        }`}
                        onClick={() => handleSelect("")}
                      >
                        {isPlaceholder ? (
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-[#A02448]">
                            <CheckIcon />
                          </span>
                        ) : (
                          <span className="h-5 w-5 shrink-0" />
                        )}
                        <span className="truncate">{placeholder}</span>
                      </button>
                    </li>
                  ) : null}

                  {filtered.length === 0 ? (
                    <li className="px-3 py-4 text-center text-sm text-[#9CA3AF]">
                      No results found
                    </li>
                  ) : (
                    filtered.map((opt) => {
                      const isActive =
                        String(opt.value) === String(value ?? "");
                      return (
                        <li key={String(opt.value)}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={isActive}
                            className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm transition ${
                              isActive
                                ? "bg-[#F5F3FF] font-medium text-[#1E1B4B]"
                                : "text-[#1E1B4B] hover:bg-[#F8FAFC]"
                            }`}
                            onClick={() => handleSelect(String(opt.value))}
                          >
                            {isActive ? (
                              <span className="flex h-5 w-5 shrink-0 items-center justify-center text-[#A02448]">
                                <CheckIcon />
                              </span>
                            ) : showOptionIcon ? (
                              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#F9F0F4] text-[#A02448]">
                                <BuildingIcon />
                              </span>
                            ) : (
                              <span className="h-5 w-5 shrink-0" />
                            )}
                            <span className="truncate">{opt.label}</span>
                          </button>
                        </li>
                      );
                    })
                  )}
                </>
              )}
            </ul>
          </div>,
          document.body
        )
      : null;

  return (
    <div className={`relative w-full ${className}`} ref={rootRef}>
      {label ? (
        <label htmlFor={selectId} className="yh-label">
          {label}
          {requiredMark ? <span className="ml-0.5 text-yh-red">*</span> : null}
        </label>
      ) : null}

      <button
        ref={buttonRef}
        id={selectId}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyPress={onKeyPress}
        onClick={() => {
          if (!disabled) setOpen((v) => !v);
        }}
        className={`yh-input flex w-full items-center justify-between gap-2 text-left ${
          open ? "!border-[#841C39] shadow-[0_0_0_2px_rgba(132,28,57,0.12)]" : ""
        } ${error ? "yh-input-error" : ""} ${
          disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"
        }`}
      >
        <span
          className={`truncate ${
            isPlaceholder ? "text-[#9CA3AF]" : "text-[#1E1B4B]"
          }`}
        >
          {displayLabel}
        </span>
        <span className="flex shrink-0 items-center gap-1 text-[#6B7280]">
          {showClear ? (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Clear selection"
              title="Clear"
              className="flex h-5 w-5 items-center justify-center rounded-full text-[#9CA3AF] transition hover:bg-[#F3F4F6] hover:text-[#6B7280]"
              onClick={handleClear}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <ClearIcon />
            </span>
          ) : null}
          <ChevronIcon open={open} />
        </span>
      </button>

      {/* Hidden native select for forms / accessibility name */}
      <select
        name={name}
        value={value ?? ""}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden
        className="hidden"
        onChange={() => undefined}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {hasValue && !selected ? (
          <option value={String(value)}>{String(value)}</option>
        ) : null}
        {flatOptions.map((opt) => (
          <option key={String(opt.value)} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {menu}

      {error ? <p className="mt-1 text-xs text-yh-red">{error}</p> : null}
    </div>
  );
};

export default YhSelect;
